/**
 * THE ACTION REGISTRY — plan-025 A-DOM (design §36.8–§36.10; ADR 0077; glossary §6.40 "Account action", "Confirm card").
 *
 * Every change the help assistant may PROPOSE in a signed-in person's account, and nothing else. Each action code names
 * one EXISTING customer route (operationId, method, path template), the ids it needs, the one value it takes, the
 * request body it builds, the read the help host previews it with, and how the person confirms. The assistant never
 * executes anything: the help host shows a confirm card, and only the person's click sends the request — from the
 * person's own browser, through the portal's own api client, with the cookie, CSRF, scope generation, the
 * `Idempotency-Key` and {@link HELP_ACTION_HEADER}. The api then serves it only when the record the help host stored
 * ({@link HelpAccountActionRecord}) names exactly that request.
 *
 * Wave A1 ships first; A2 only with the owner's yes (plan-025 OD-5). The never-actionable list lives in the arch rule
 * `help-account-registry`, which fails CI when an entry here reaches one.
 *
 * Imports nothing, so the widget's lazy panel chunk can read the registry to check a frame before it renders a card.
 */
/** The header the person's browser sends with a confirmed request (lower case, as the api reads request headers). */
export const HELP_ACTION_HEADER = "x-wetalk-help-action";
/** The ids an action may need, from earlier read results. */
export const HELP_ACCOUNT_ACTION_ID_ARGUMENT = ["voice_agent_id", "campaign_id", "gap_id", "conversation_flag_id", "agent_version_id"];
export const HELP_ACCOUNT_ACTION = Object.freeze({
    pause_voice_agent: {
        operationId: "pauseVoiceAgent",
        method: "POST",
        pathTemplate: "/v1/agents/{voice_agent_id}/pause",
        wave: "a1",
        targetKind: "voice_agent",
        requiredId: ["voice_agent_id"],
        valueKind: "none",
        previewRead: "voice_agent",
        confirmation: "click",
    },
    resume_voice_agent: {
        operationId: "resumeVoiceAgent",
        method: "POST",
        pathTemplate: "/v1/agents/{voice_agent_id}/resume",
        wave: "a1",
        targetKind: "voice_agent",
        requiredId: ["voice_agent_id"],
        valueKind: "none",
        previewRead: "voice_agent",
        confirmation: "click",
    },
    set_line_limit: {
        operationId: "setVoiceAgentChannelUnitLimit",
        method: "PUT",
        pathTemplate: "/v1/agents/{voice_agent_id}/concurrency",
        wave: "a1",
        targetKind: "voice_agent",
        requiredId: ["voice_agent_id"],
        valueKind: "line_limit",
        previewRead: "line_limit",
        confirmation: "click",
    },
    pause_campaign: {
        operationId: "setCampaignPaused",
        method: "PUT",
        pathTemplate: "/v1/campaigns/{campaign_id}/paused",
        wave: "a1",
        targetKind: "campaign",
        requiredId: ["campaign_id"],
        valueKind: "none",
        previewRead: "campaign",
        confirmation: "click",
    },
    resume_campaign: {
        operationId: "setCampaignPaused",
        method: "PUT",
        pathTemplate: "/v1/campaigns/{campaign_id}/paused",
        wave: "a1",
        targetKind: "campaign",
        requiredId: ["campaign_id"],
        valueKind: "none",
        previewRead: "campaign",
        confirmation: "click",
    },
    answer_gap: {
        operationId: "answerGap",
        method: "POST",
        pathTemplate: "/v1/agents/{voice_agent_id}/gaps/{gap_id}/answer",
        wave: "a1",
        targetKind: "gap",
        requiredId: ["gap_id", "voice_agent_id"],
        valueKind: "person_text",
        previewRead: "gaps",
        confirmation: "click",
    },
    dismiss_gap: {
        operationId: "dismissGap",
        method: "POST",
        pathTemplate: "/v1/agents/{voice_agent_id}/gaps/{gap_id}/dismiss",
        wave: "a1",
        targetKind: "gap",
        requiredId: ["gap_id", "voice_agent_id"],
        valueKind: "none",
        previewRead: "gaps",
        confirmation: "click",
    },
    resolve_conversation_flag: {
        operationId: "resolveConversationFlag",
        method: "POST",
        pathTemplate: "/v1/agents/{voice_agent_id}/flags/{conversation_flag_id}/resolve",
        wave: "a1",
        targetKind: "conversation_flag",
        requiredId: ["conversation_flag_id", "voice_agent_id"],
        valueKind: "none",
        previewRead: "conversation_flags",
        confirmation: "click",
    },
    publish_draft: {
        operationId: "publishAgentVersion",
        method: "POST",
        pathTemplate: "/v1/agents/{voice_agent_id}/versions/{agent_version_id}/publish",
        wave: "a2",
        targetKind: "voice_agent",
        requiredId: ["voice_agent_id", "agent_version_id"],
        valueKind: "none",
        previewRead: "voice_agent",
        confirmation: "click",
    },
});
export const HELP_ACCOUNT_ACTION_CODE = Object.freeze(Object.keys(HELP_ACCOUNT_ACTION));
/** The wave of changes offered now (plan-025 OD-5): A1. A2 waits for a separate yes from the owner. */
export const HELP_ACCOUNT_ACTION_ACTIVE_WAVE = "a1";
export function isHelpAccountActionCode(value) {
    return typeof value === "string" && Object.hasOwn(HELP_ACCOUNT_ACTION, value);
}
/** The action codes offered while `wave` is on (A2 implies A1). */
export function helpAccountActionCodesOf(wave) {
    return HELP_ACCOUNT_ACTION_CODE.filter((code) => wave === "a2" || HELP_ACCOUNT_ACTION[code].wave === "a1");
}
/** At most this many characters of text the person wrote, as an action's value (`answer_gap`: the route's own limit). */
export const HELP_ACCOUNT_ACTION_TEXT_CHAR_LIMIT = 1_000;
const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u;
/** Parses `propose_account_action`'s argument JSON exactly. */
export function parseHelpAccountActionArgument(argumentJson) {
    let value;
    try {
        value = JSON.parse(argumentJson);
    }
    catch {
        return "argument_invalid";
    }
    if (typeof value !== "object" || value === null || Array.isArray(value))
        return "argument_invalid";
    const f = value;
    const action = f["action"];
    if (!isHelpAccountActionCode(action))
        return "action_not_offered";
    const entry = HELP_ACCOUNT_ACTION[action];
    const id = {};
    let given = null;
    for (const [key, one] of Object.entries(f)) {
        if (key === "action" || one === null || one === undefined)
            continue;
        if (key === "value") {
            if (entry.valueKind === "none")
                return "argument_invalid";
            if (entry.valueKind === "line_limit") {
                if (one === "version_ceiling")
                    given = one;
                else if (typeof one === "number" && Number.isSafeInteger(one) && one >= 1 && one <= 10_000)
                    given = one;
                else
                    return "argument_invalid";
            }
            else {
                if (typeof one !== "string")
                    return "argument_invalid";
                const text = one.trim();
                if (text === "" || Array.from(text).length > HELP_ACCOUNT_ACTION_TEXT_CHAR_LIMIT)
                    return "argument_invalid";
                given = text;
            }
            continue;
        }
        if (!HELP_ACCOUNT_ACTION_ID_ARGUMENT.includes(key))
            return "argument_invalid";
        if (!entry.requiredId.includes(key))
            return "argument_invalid";
        if (typeof one !== "string" || !UUID_SHAPE.test(one.toLowerCase()))
            return "argument_invalid";
        id[key] = one.toLowerCase();
    }
    if (entry.requiredId.some((key) => id[key] === undefined))
        return "argument_invalid";
    if (entry.valueKind !== "none" && given === null)
        return "argument_invalid";
    return { action, id, value: given };
}
/** The concrete path of a parsed action. */
export function helpAccountActionPathOf(argument) {
    const entry = HELP_ACCOUNT_ACTION[argument.action];
    let path = entry.pathTemplate;
    for (const key of entry.requiredId) {
        const one = argument.id[key];
        if (one === undefined)
            throw new Error(`action ${argument.action}: ${key} was not parsed`);
        path = path.replace(`{${key}}`, one);
    }
    if (/\{[a-z_]+\}/u.test(path))
        throw new Error(`action ${argument.action}: the path template names a parameter no argument fills`);
    return path;
}
/**
 * The request body of a parsed action, as one JSON string the browser sends unchanged (its SHA-256 is in the record),
 * or `null` for a route that takes none. `liveVersionSeq` is the preview's, for `publish_draft`.
 */
export function helpAccountActionBodyOf(argument, preview) {
    switch (argument.action) {
        case "pause_voice_agent":
        case "resume_voice_agent":
            return JSON.stringify({});
        case "set_line_limit":
            return JSON.stringify({ channel_unit_limit: argument.value === "version_ceiling" ? null : argument.value });
        case "pause_campaign":
            return JSON.stringify({ paused: true });
        case "resume_campaign":
            return JSON.stringify({ paused: false });
        case "answer_gap":
            return JSON.stringify({ answer_text: argument.value });
        case "dismiss_gap":
        case "resolve_conversation_flag":
            return null;
        case "publish_draft":
            if (preview.liveVersionSeq === null)
                throw new Error("publish_draft needs the live version's sequence from its preview");
            return JSON.stringify({ expected_live_version_seq: preview.liveVersionSeq });
    }
}
/** `help_account_action:{help_action_id}`. */
export function helpAccountActionKeyOf(helpActionId) {
    if (!UUID_SHAPE.test(helpActionId))
        throw new RangeError("a help action id is a UUID");
    return `help_account_action:${helpActionId}`;
}
/** `help_account_action_state:{help_action_id}`: `shown` → `claimed` → `executed:<status>` | `failed:<status>:<code>`. */
export function helpAccountActionStateKeyOf(helpActionId) {
    if (!UUID_SHAPE.test(helpActionId))
        throw new RangeError("a help action id is a UUID");
    return `help_account_action_state:${helpActionId}`;
}
export const HELP_ACCOUNT_ACTION_STATE_SHOWN = "shown";
export const HELP_ACCOUNT_ACTION_STATE_CLAIMED = "claimed";
export function helpAccountActionStateTextOf(state) {
    switch (state.kind) {
        case "shown":
        case "claimed":
            return state.kind;
        case "executed":
            return `executed:${String(state.status)}`;
        case "failed":
            return `failed:${String(state.status)}:${state.code}`;
    }
}
export function parseHelpAccountActionState(text) {
    if (text === "shown" || text === "claimed")
        return { kind: text };
    const executed = /^executed:(\d{3})$/u.exec(text);
    if (executed !== null)
        return { kind: "executed", status: Number(executed[1]) };
    const failed = /^failed:(\d{3}):([a-z][a-z0-9_]{0,63})$/u.exec(text);
    if (failed !== null)
        return { kind: "failed", status: Number(failed[1]), code: failed[2] };
    return null;
}
const RECORD_KEY = [
    "helpActionId",
    "conversationId",
    "action",
    "personId",
    "accountId",
    "workspaceId",
    "scopeGeneration",
    "operationId",
    "method",
    "path",
    "bodySha256",
    "delegationSha256",
    "expiresAt",
];
export function encodeHelpAccountActionRecord(record) {
    return JSON.stringify(record);
}
/** The record read from the shared cache, or `null` when it is not exactly one. */
export function decodeHelpAccountActionRecord(text) {
    let value;
    try {
        value = JSON.parse(text);
    }
    catch {
        return null;
    }
    if (typeof value !== "object" || value === null || Array.isArray(value))
        return null;
    const f = value;
    const own = Object.keys(f);
    if (own.length !== RECORD_KEY.length || !RECORD_KEY.every((key) => own.includes(key)))
        return null;
    for (const key of ["helpActionId", "conversationId", "personId", "accountId"]) {
        if (typeof f[key] !== "string" || !UUID_SHAPE.test(f[key]))
            return null;
    }
    if (f["workspaceId"] !== null && (typeof f["workspaceId"] !== "string" || !UUID_SHAPE.test(f["workspaceId"])))
        return null;
    if (!isHelpAccountActionCode(f["action"]))
        return null;
    const entry = HELP_ACCOUNT_ACTION[f["action"]];
    if (f["operationId"] !== entry.operationId || f["method"] !== entry.method)
        return null;
    if (typeof f["path"] !== "string" || !f["path"].startsWith("/v1/"))
        return null;
    if (typeof f["bodySha256"] !== "string" || !/^[0-9a-f]{64}$/u.test(f["bodySha256"]))
        return null;
    if (typeof f["delegationSha256"] !== "string" || !/^[0-9a-f]{64}$/u.test(f["delegationSha256"]))
        return null;
    const generation = f["scopeGeneration"];
    if (typeof generation !== "number" || !Number.isSafeInteger(generation) || generation < 0)
        return null;
    if (typeof f["expiresAt"] !== "string" || Number.isNaN(Date.parse(f["expiresAt"])))
        return null;
    return f;
}
// ── the card's vocabulary (codes the widget renders from its own EN/EL catalog) ─────────────────────────────────────
/** A card row's field: the widget's catalog names it. */
export const HELP_ACCOUNT_ACTION_FIELD = ["state", "line_limit", "question", "answer", "flag"];
/** Why `propose_account_action` showed nothing (the model hears it; `help_action.refusal_reason` records it). */
export const HELP_ACCOUNT_ACTION_REFUSAL = [
    "unavailable",
    "action_not_offered",
    "argument_invalid",
    "value_not_from_person",
    "one_at_a_time",
    "after_untrusted_text",
    "limit_reached",
    "already_so",
    "precondition",
    "not_permitted",
    "not_found",
    "workspace_required",
];
//# sourceMappingURL=account-action.js.map